import { PrefilledLineData } from '../../types/prefilled-line/PrefilledLineData';
import React, { useMemo } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { PrefilledLineItem } from './PrefilledLineItem';
import { PrefilledLineInsertionDropClone } from './PrefilledLineInsertionDropClone';

export interface PrefilledLinesListProps {
  lines: PrefilledLineData[];
  selectedIndex: number | null;
  setSelectedIndex?: (index: number) => void;
}

interface ItemData {
  lines: PrefilledLineData[];
  selectedIndex: number | null;
  onItemClicked: (index: number) => void;
}

const renderRow = (props: ListChildComponentProps) => {
  const { index, style, data: _data } = props;
  const data = _data as ItemData;
  const item = data.lines[index];

  return (
    <Draggable
      draggableId={'prefilled-lines-list-item-' + item.uuid}
      index={index}
      key={item.uuid}
    >
      {(provided) => (
        <PrefilledLineItem
          item={item}
          onItemClicked={() => data.onItemClicked(index)}
          selected={data.selectedIndex === index}
          style={style}
          provided={provided}
        />
      )}
    </Draggable>
  );
};

/**
 * Displays a list of prefilled lines.
 * Must be wrapped into a DragDropContext to make D&D work.
 * @param props
 * @constructor
 */
export const PrefilledLinesList = (props: PrefilledLinesListProps) => {
  const { lines, selectedIndex, setSelectedIndex } = props;
  const data: ItemData = useMemo(
    () => ({
      lines,
      selectedIndex,
      onItemClicked: (index) => {
        setSelectedIndex?.(index);
      },
    }),
    [lines, selectedIndex, setSelectedIndex],
  );
  return (
    <AutoSizer
      renderProp={({ width, height }) => (
        <Droppable
          droppableId={'prefilled-lines'}
          mode={'virtual'}
          renderClone={(provided, snapshot, rubric) => {
            if (snapshot.draggingOver === 'account-table') {
              return (
                <PrefilledLineInsertionDropClone
                  item={lines[rubric.source.index]}
                  provided={provided}
                />
              );
            } else {
              return (
                <PrefilledLineItem
                  item={lines[rubric.source.index]}
                  provided={provided}
                  isDragging={true}
                />
              );
            }
          }}
        >
          {(provided) => (
            <FixedSizeList
              outerRef={provided.innerRef}
              height={height ?? 0}
              width={width ?? 0}
              itemSize={36}
              itemCount={lines.length}
              itemData={data}
            >
              {renderRow}
            </FixedSizeList>
          )}
        </Droppable>
      )}
    />
  );
};
